import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  Person,
  CreatePersonData,
  UpdatePersonData,
  GetPeopleResponseSchema,
  GetPersonResponseSchema,
  CreatePersonResponseSchema,
  UpdatePersonResponseSchema,
} from "@forgepoint/shared";
import { useCompaniesStore } from "@/modules/companies/companiesStore";

interface PersonLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  getError: string | null;
  saveError: string | null;
  deleteError: string | null;
}

interface FetchPeopleParams {
  search?: string;
  companyId?: string;
  limit?: number;
  offset?: number;
}

interface PeopleStore {
  people: Map<string, Person>;
  personLoadingStates: Map<string, PersonLoadingState>;
  listLoading: boolean;
  listError: string | null;

  fetchPeople: (
    params?: FetchPeopleParams,
  ) => Promise<{ ids: string[]; total: number }>;
  fetchPerson: (id: string) => Promise<void>;
  createPerson: (
    data: CreatePersonData,
  ) => Promise<{ person: Person | null; error: string | null }>;
  updatePerson: (id: string, data: UpdatePersonData) => Promise<Person | null>;
  deletePerson: (id: string) => Promise<boolean>;
  setPeople: (people: Map<string, Person>) => void;
  upsertPeople: (people: Person[]) => void;
}

const getInitialLoadingState = (): PersonLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  getError: null,
  saveError: null,
  deleteError: null,
});

/**
 * Clears a company's hydrated primaryContact pointer when it points at the
 * given person.
 */
const clearPrimaryContactIfPointsTo = (
  companyId: string | null,
  personId: string,
): void => {
  if (!companyId) {
    return;
  }
  const { companies, setCompanyPrimaryContact } = useCompaniesStore.getState();
  if (companies.get(companyId)?.primaryContact?.id === personId) {
    setCompanyPrimaryContact(companyId, null);
  }
};

/**
 * Mirrors a person mutation into the companies store's hydrated primaryContact
 * so the Companies list stays accurate without a refetch. Clears the previous
 * company's pointer when a primary person moves between companies.
 */
const syncCompanyPrimaryContact = (
  person: Person,
  previousPerson?: Person,
): void => {
  if (previousPerson && previousPerson.companyId !== person.companyId) {
    clearPrimaryContactIfPointsTo(previousPerson.companyId, person.id);
  }

  const { companies, setCompanyPrimaryContact } = useCompaniesStore.getState();
  if (!person.companyId || !companies.has(person.companyId)) {
    return;
  }

  if (person.isPrimaryCompanyContact) {
    setCompanyPrimaryContact(person.companyId, {
      id: person.id,
      firstName: person.firstName,
      lastName: person.lastName,
      email: person.email,
    });
    return;
  }

  clearPrimaryContactIfPointsTo(person.companyId, person.id);
};

const upsertWithPrimarySync = (
  people: Map<string, Person>,
  person: Person,
): Map<string, Person> => {
  const next = new Map(people);

  if (person.companyId && person.isPrimaryCompanyContact) {
    next.forEach((existing, key) => {
      if (
        key !== person.id &&
        existing.companyId === person.companyId &&
        existing.isPrimaryCompanyContact
      ) {
        next.set(key, {
          ...existing,
          isPrimaryCompanyContact: false,
        });
      }
    });
  }

  next.set(person.id, person);
  return next;
};

export const usePeopleStore = create<PeopleStore>((set, get) => ({
  people: new Map(),
  personLoadingStates: new Map(),
  listLoading: false,
  listError: null,

  setPeople: (people: Map<string, Person>) => {
    set({ people });
  },

  upsertPeople: (people: Person[]) => {
    if (people.length === 0) {
      return;
    }
    set((state) => {
      const next = new Map(state.people);
      people.forEach((person) => next.set(person.id, person));
      return { people: next };
    });
  },

  fetchPeople: async (params?: FetchPeopleParams) => {
    set({ listLoading: true, listError: null });

    try {
      const queryParams = new URLSearchParams();
      if (params?.search) {
        queryParams.append("search", params.search);
      }
      if (params?.companyId) {
        queryParams.append("companyId", params.companyId);
      }
      if (params?.limit !== undefined) {
        queryParams.append("limit", String(params.limit));
      }
      if (params?.offset !== undefined) {
        queryParams.append("offset", String(params.offset));
      }
      const queryString = queryParams.toString();
      const url = queryString ? `people?${queryString}` : "people";

      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        const data = GetPeopleResponseSchema.parse(response.data);
        set((state) => {
          const peopleMap = new Map(state.people);
          data.people.forEach((person) => {
            peopleMap.set(person.id, person);
          });
          return { people: peopleMap, listLoading: false };
        });
        return {
          ids: data.people.map((person) => person.id),
          total: data.total,
        };
      }

      set({ listError: "Failed to fetch people", listLoading: false });
      return { ids: [], total: 0 };
    } catch (error) {
      captureException(error as Error);
      set({ listError: "Failed to fetch people", listLoading: false });
      return { ids: [], total: 0 };
    }
  },

  fetchPerson: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.personLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { personLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`people/${id}`, "GET");

      if (response.ok && response.data) {
        const data = GetPersonResponseSchema.parse(response.data);
        const previousPerson = get().people.get(id);
        set((state) => {
          const updatedPeople = upsertWithPrimarySync(
            state.people,
            data.person,
          );
          const loadingStates = new Map(state.personLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          loadingStates.set(id, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });
          return {
            people: updatedPeople,
            personLoadingStates: loadingStates,
          };
        });
        syncCompanyPrimaryContact(data.person, previousPerson);
        return;
      }

      set((state) => {
        const loadingStates = new Map(state.personLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch person",
        });
        return { personLoadingStates: loadingStates };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.personLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch person",
        });
        return { personLoadingStates: loadingStates };
      });
    }
  },

  createPerson: async (data: CreatePersonData) => {
    try {
      const response = await fetchApi("people", "POST", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = CreatePersonResponseSchema.parse(response.data);
        set((state) => {
          const updatedPeople = upsertWithPrimarySync(
            state.people,
            result.person,
          );
          return { people: updatedPeople };
        });
        syncCompanyPrimaryContact(result.person);
        return { person: result.person, error: null };
      }

      return {
        person: null,
        error: response.error ?? "Failed to create person",
      };
    } catch (error) {
      captureException(error as Error);
      return { person: null, error: "Failed to create person" };
    }
  },

  updatePerson: async (id: string, data: UpdatePersonData) => {
    set((state) => {
      const loadingStates = new Map(state.personLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { personLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`people/${id}`, "PUT", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = UpdatePersonResponseSchema.parse(response.data);
        const previousPerson = get().people.get(id);
        set((state) => {
          const updatedPeople = upsertWithPrimarySync(
            state.people,
            result.person,
          );
          const loadingStates = new Map(state.personLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          loadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return {
            people: updatedPeople,
            personLoadingStates: loadingStates,
          };
        });
        syncCompanyPrimaryContact(result.person, previousPerson);
        return result.person;
      }

      set((state) => {
        const loadingStates = new Map(state.personLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update person",
        });
        return { personLoadingStates: loadingStates };
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.personLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update person",
        });
        return { personLoadingStates: loadingStates };
      });
      return null;
    }
  },

  deletePerson: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.personLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingDelete: true,
        deleteError: null,
      });
      return { personLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`people/${id}`, "DELETE");

      if (response.ok) {
        const deletedPerson = get().people.get(id);
        set((state) => {
          const people = new Map(state.people);
          const loadingStates = new Map(state.personLoadingStates);
          people.delete(id);
          loadingStates.delete(id);
          return {
            people,
            personLoadingStates: loadingStates,
          };
        });
        if (deletedPerson) {
          clearPrimaryContactIfPointsTo(deletedPerson.companyId, id);
        }
        return true;
      }

      set((state) => {
        const loadingStates = new Map(state.personLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete person",
        });
        return { personLoadingStates: loadingStates };
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.personLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete person",
        });
        return { personLoadingStates: loadingStates };
      });
      return false;
    }
  },
}));
